import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Card, CardContent } from "./components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./components/ui/carousel";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { 
  Phone, 
  Mail, 
  CheckCircle, 
  ArrowRight, 
  Globe, 
  MessageSquare, 
  Palette, 
  CreditCard,
  Menu,
  X,
  ChevronDown
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Portfolio items
const portfolioItems = [
  {
    name: "cryptoyuntie.com",
    url: "https://cryptoyuntie.com",
    image: "https://images.unsplash.com/photo-1712904311011-66c73f2ed065?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxsYXB0b3AlMjBtb2NrdXAlMjBkZXNrfGVufDB8fHx8MTc3NTc0NjA5Mnww&ixlib=rb-4.1.0&q=85",
    description: "Crypto community platform"
  },
  {
    name: "cryptobagtracker.io",
    url: "https://cryptobagtracker.io",
    image: "https://images.unsplash.com/photo-1746792651958-bfe08cf1d230?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxsYXB0b3AlMjBtb2NrdXAlMjBkZXNrfGVufDB8fHx8MTc3NTc0NjA5Mnww&ixlib=rb-4.1.0&q=85",
    description: "Portfolio tracking application"
  }
];

// Navigation component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setMobileMenuOpen(false);
    }
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-header shadow-sm" : "bg-transparent"
      }`}
      data-testid="navigation-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20">
          <a href="/" className="flex items-center" aria-label="Acoretek Services" data-testid="logo">
            <img
              src="/assets/logo-wordmark.png"
              alt="Acoretek Services"
              className="h-10 md:h-12 w-auto object-contain"
              data-testid="logo-image"
            />
          </a>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection("services")}
              className="font-body text-sm font-medium hover:text-[#007AFF] transition-colors"
              data-testid="nav-services"
            >
              Services
            </button>
            <button 
              onClick={() => scrollToSection("pricing")}
              className="font-body text-sm font-medium hover:text-[#007AFF] transition-colors"
              data-testid="nav-pricing"
            >
              Pricing
            </button>
            <button 
              onClick={() => scrollToSection("faq")}
              className="font-body text-sm font-medium hover:text-[#007AFF] transition-colors"
              data-testid="nav-faq"
            >
              FAQ
            </button>
            <button 
              onClick={() => scrollToSection("intake")}
              className="font-body text-sm font-medium hover:text-[#007AFF] transition-colors"
              data-testid="nav-intake"
            >
              New Site
            </button>
            <button 
              onClick={() => scrollToSection("contact")}
              className="font-body text-sm font-medium hover:text-[#007AFF] transition-colors"
              data-testid="nav-contact"
            >
              Contact
            </button>
            <Button 
              onClick={() => scrollToSection("pricing")}
              className="bg-[#0A0A0A] text-white hover:bg-[#007AFF] rounded-none px-6"
              data-testid="nav-get-started"
            >
              Get Started
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden pb-6 flex flex-col gap-4 bg-white" data-testid="mobile-menu">
            <button onClick={() => scrollToSection("services")} className="text-left py-2 font-medium">Services</button>
            <button onClick={() => scrollToSection("pricing")} className="text-left py-2 font-medium">Pricing</button>
            <button onClick={() => scrollToSection("faq")} className="text-left py-2 font-medium">FAQ</button>
            <button onClick={() => scrollToSection("intake")} className="text-left py-2 font-medium">New Site</button>
            <button onClick={() => scrollToSection("contact")} className="text-left py-2 font-medium">Contact</button>
            <Button onClick={() => scrollToSection("pricing")} className="bg-[#0A0A0A] text-white rounded-none w-full">
              Get Started
            </Button>
          </nav>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="min-h-screen flex items-center pt-20" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="space-y-8">
            <div className="animate-fade-in-up">
              <p className="font-body text-sm font-medium text-[#6B7280] uppercase tracking-widest mb-4">
                Web Development Services
              </p>
              <h1 className="font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none text-[#0A0A0A]">
                Websites That
                <br />
                <span className="price-highlight">Work For You</span>
              </h1>
            </div>
            
            <p className="font-body text-lg md:text-xl text-[#6B7280] leading-relaxed max-w-lg animate-fade-in-up animation-delay-100">
              Professional websites with appointments, applications, and galleries. 
              Everything you need to showcase your business online.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
              <Button 
                onClick={() => scrollToSection("pricing")}
                size="lg"
                className="bg-[#007AFF] text-white hover:bg-[#0055CC] rounded-none px-8 py-6 text-base font-medium"
                data-testid="hero-cta-primary"
              >
                Start at $88
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => scrollToSection("contact")}
                variant="outline"
                size="lg"
                className="rounded-none px-8 py-6 text-base font-medium border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
                data-testid="hero-cta-secondary"
              >
                Contact Us
              </Button>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-4 animate-fade-in-up animation-delay-300">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-[#007AFF]" />
                <span className="font-body text-sm">We build first, you pay later</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-[#007AFF]" />
                <span className="font-body text-sm">10 Changes Included</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-[#007AFF]" />
                <span className="font-body text-sm">No monthly fees like StyleSeat</span>
              </div>
            </div>
          </div>

          <div className="hidden lg:block animate-fade-in-up animation-delay-200">
            <div className="relative">
              <div className="absolute -inset-4 bg-[#007AFF]/10 rounded-sm"></div>
              <img 
                src="https://images.unsplash.com/photo-1763191213523-1489179a1088?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxjcmVhdGl2ZSUyMHN0YXJ0dXAlMjBvZmZpY2V8ZW58MHx8fHwxNzc1NzQ2MDkyfDA&ixlib=rb-4.1.0&q=85"
                alt="Modern workspace"
                className="relative w-full h-[500px] object-cover rounded-sm"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Globe,
      title: "Custom Websites",
      description: "Fully responsive websites tailored to your brand and business needs."
    },
    {
      icon: MessageSquare,
      title: "Appointments",
      description: "Integrated booking system with support for multiple staff members. Customers choose their preferred barber, stylist, or technician."
    },
    {
      icon: CreditCard,
      title: "Payments",
      description: "Accept payments through Stripe and Cash App. Get paid easily by your customers online."
    },
    {
      icon: Palette,
      title: "Work Galleries",
      description: "Beautiful galleries to showcase your portfolio and projects."
    }
  ];

  return (
    <section id="services" className="py-24 md:py-32 bg-[#F3F4F6]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <p className="font-body text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
            What We Offer
          </p>
          <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#0A0A0A]">
            Everything You Need
          </h2>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <Card 
              key={index} 
              className="bg-white border-0 rounded-none shadow-none hover:shadow-xl transition-all duration-300"
              data-testid={`service-card-${index}`}
            >
              <CardContent className="p-8">
                <div className="w-14 h-14 bg-[#007AFF] flex items-center justify-center mb-6">
                  <service.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="font-heading font-bold text-xl mb-3 text-[#0A0A0A]">
                  {service.title}
                </h3>
                <p className="font-body text-[#6B7280] leading-relaxed">
                  {service.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Multi-staff highlight */}
        <div className="mt-12 text-center p-6 bg-white border-l-4 border-[#007AFF]">
          <p className="font-body text-lg text-[#0A0A0A]">
            <span className="font-bold">Multi-Staff Support:</span> Perfect for barbershops, salons, and clinics — customers can book with any team member!
          </p>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onCheckout, onCryptoCheckout, isLoading }) => {
  const packages = [
    {
      id: "starter",
      name: "Starter Website",
      price: "$88",
      description: "Complete website with everything you need to get started",
      features: [
        "Custom website (up to 3 pages)",
        "Your logo on your site",
        "Mobile responsive",
        "Appointments integration",
        "Up to 5 staff members",
        "Accept Stripe & Cash App",
        "10 changes included"
      ],
      highlighted: true
    },
    {
      id: "hosting",
      name: "Annual Hosting",
      price: "$44.88",
      period: "/year",
      description: "Keep your website live and running smoothly",
      features: [
        "Fast & reliable hosting",
        "SSL certificate",
        "Daily backups",
        "Free domain with website"
      ],
      highlighted: false
    },
    {
      id: "sms_addon",
      name: "SMS Add-on",
      price: "$10.88",
      period: "/year",
      description: "Keep in touch with your customers via SMS",
      features: [
        "SMS notifications",
        "Appointment reminders",
        "Marketing messages",
        "Easy setup"
      ],
      highlighted: false
    },
    {
      id: "changes_pack",
      name: "Changes Pack",
      price: "$5.88",
      description: "Need more changes after your initial build?",
      features: [
        "5 cosmetic changes",
        "Text updates",
        "Image swaps",
        "Color adjustments"
      ],
      highlighted: false
    },
    {
      id: "ai_agent",
      name: "AI Agent",
      price: "$288",
      period: "/month",
      description: "Custom AI assistant built for your business",
      features: [
        "24/7 customer support",
        "Answers FAQs instantly",
        "Books appointments",
        "Custom trained for you"
      ],
      highlighted: false
    }
  ];

  return (
    <section id="pricing" className="py-24 md:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <p className="font-body text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
            Simple Pricing
          </p>
          <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#0A0A0A] mb-4">
            Transparent & Affordable
          </h2>
          <p className="font-body text-lg text-[#6B7280] max-w-2xl mx-auto">
            No hidden fees. Get started with a professional website today.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
          {packages.map((pkg, index) => (
            <Card 
              key={pkg.id}
              className={`rounded-none border-2 ${
                pkg.highlighted 
                  ? "border-[#007AFF] shadow-xl" 
                  : "border-[#E5E7EB]"
              }`}
              data-testid={`pricing-card-${pkg.id}`}
            >
              <CardContent className="p-6">
                {pkg.highlighted && (
                  <div className="bg-[#007AFF] text-white text-xs font-bold uppercase tracking-wider py-1 px-3 inline-block mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="font-heading font-bold text-lg mb-2 text-[#0A0A0A]">
                  {pkg.name}
                </h3>
                <div className="flex items-baseline gap-1 mb-3">
                  <span className="font-heading font-black text-3xl text-[#0A0A0A]">
                    {pkg.price}
                  </span>
                  {pkg.period && (
                    <span className="font-body text-[#6B7280] text-sm">{pkg.period}</span>
                  )}
                </div>
                <p className="font-body text-sm text-[#6B7280] mb-4">
                  {pkg.description}
                </p>
                <ul className="space-y-2 mb-6">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-[#007AFF] flex-shrink-0" />
                      <span className="font-body text-xs text-[#0A0A0A]">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button 
                  onClick={() => onCheckout(pkg.id)}
                  disabled={isLoading}
                  className={`w-full rounded-none py-5 font-medium text-sm ${
                    pkg.highlighted 
                      ? "bg-[#007AFF] hover:bg-[#0055CC] text-white" 
                      : "bg-[#0A0A0A] hover:bg-[#007AFF] text-white"
                  }`}
                  data-testid={`checkout-btn-${pkg.id}`}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner"></span>
                      Processing...
                    </span>
                  ) : (
                    <>
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Crypto Payment Option */}
        <div className="mt-12 p-8 border-2 border-dashed border-[#E5E7EB] bg-[#F9FAFB]" data-testid="crypto-section">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading font-bold text-xl text-[#0A0A0A] mb-2">
                Pay with Crypto
              </h3>
              <p className="font-body text-[#6B7280]">
                Get the complete website package for <span className="font-bold text-[#0A0A0A]">$99.88</span> using cryptocurrency (BTC, ETH, USDT & more)
              </p>
            </div>
            <Button 
              onClick={onCryptoCheckout}
              disabled={isLoading}
              className="bg-[#F7931A] hover:bg-[#E8850F] text-white rounded-none px-8 py-6 font-medium whitespace-nowrap"
              data-testid="crypto-checkout-btn"
            >
              Pay with Crypto
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="font-body text-[#6B7280]">
            Secure payments powered by <span className="font-semibold text-[#0A0A0A]">Stripe</span> & <span className="font-semibold text-[#0A0A0A]">NOWPayments</span>
          </p>
        </div>
      </div>
    </section>
  );
};

// Portfolio Section
const PortfolioSection = () => {
  return (
    <section id="portfolio" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="portfolio-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-16">
          <p className="font-body text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
            Our Portfolio
          </p>
          <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight text-white mb-4">
            Recent Work
          </h2>
          <p className="font-body text-lg text-[#9CA3AF] max-w-2xl mx-auto">
            Check out some of the websites we&apos;ve built for our clients.
          </p>
        </div>

        <Carousel className="w-full max-w-4xl mx-auto" data-testid="portfolio-carousel">
          <CarouselContent>
            {portfolioItems.map((item, index) => (
              <CarouselItem key={index}>
                <Card className="bg-transparent border-0 rounded-none portfolio-card" data-testid={`portfolio-item-${index}`}>
                  <CardContent className="p-0">
                    <div className="relative group overflow-hidden">
                      <img 
                        src={item.image}
                        alt={item.name}
                        className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-8">
                        <h3 className="font-heading font-black text-2xl text-white mb-2">
                          {item.name}
                        </h3>
                        <p className="font-body text-[#9CA3AF] mb-4">
                          {item.description}
                        </p>
                        <a 
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-[#007AFF] font-medium hover:underline"
                          data-testid={`portfolio-link-${index}`}
                        >
                          Visit Site
                          <ArrowRight className="h-4 w-4" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </CarouselItem>
            ))}
          </CarouselContent>
          <CarouselPrevious 
            className="left-4 bg-white/10 border-0 text-white hover:bg-white hover:text-[#0A0A0A]" 
            data-testid="carousel-prev"
          />
          <CarouselNext 
            className="right-4 bg-white/10 border-0 text-white hover:bg-white hover:text-[#0A0A0A]" 
            data-testid="carousel-next"
          />
        </Carousel>
      </div>
    </section>
  );
};

// Email validation helper
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// FAQ Section
const FAQSection = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "How long does it take to build my website?",
      answer: "Most websites are completed within 5-7 business days after we receive all your information and content. Simple sites can be done even faster!"
    },
    {
      question: "What do I need to get started?",
      answer: "Just fill out our New Site Request form with your business info, services, and any logo you have. We'll handle the rest and reach out if we need anything else."
    },
    {
      question: "Do I really pay after the site is built?",
      answer: "Yes! We build first, you pay later. You'll see your completed website before making any payment. No risk to you."
    },
    {
      question: "Why choose Acoretek over StyleSeat or Booksy?",
      answer: "Unlike StyleSeat or Booksy, you OWN your website - no monthly platform fees eating into your profits, no competing with other stylists on the same app, and your clients book directly with YOU. One-time $88 vs $30+/month forever. Plus you get Stripe & Cash App payments without their 2.5%+ booking fees."
    },
    {
      question: "Can I make changes after the site is live?",
      answer: "Absolutely! Your first 10 changes are included free. After that, you can purchase a Changes Pack (5 changes for $5.88) anytime."
    },
    {
      question: "What's included in the $88 Starter Website?",
      answer: "You get a custom website (up to 3 pages), your logo displayed, mobile-friendly design, appointment booking, support for up to 5 staff members, Stripe & Cash App payments, and 10 free changes."
    },
    {
      question: "Do I need to pay for hosting separately?",
      answer: "Yes, hosting is $44.88/year which keeps your site live 24/7 with SSL security, daily backups, and a free domain with your website purchase."
    },
    {
      question: "What if I don't have a logo?",
      answer: "No problem! You can still get your website built. We recommend getting a logo designed separately, and we can add it to your site later as one of your free changes."
    },
    {
      question: "Can customers book appointments with specific staff members?",
      answer: "Yes! Your website supports multiple staff members (up to 5). Customers can choose who they want to book with - perfect for barbershops, salons, and clinics."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 md:py-32 bg-white" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-12">
          <p className="font-body text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
            FAQ
          </p>
          <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#0A0A0A]">
            Common Questions
          </h2>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div 
              key={index}
              className="border-2 border-[#E5E7EB] overflow-hidden"
              data-testid={`faq-item-${index}`}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-6 text-left hover:bg-[#F9FAFB] transition-colors"
              >
                <span className="font-heading font-bold text-[#0A0A0A] pr-4">
                  {faq.question}
                </span>
                <ChevronDown 
                  className={`h-5 w-5 text-[#007AFF] flex-shrink-0 transition-transform ${
                    openIndex === index ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-6">
                  <p className="font-body text-[#6B7280] leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!isValidEmail(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("You have sent a message to Acoretek Services!");
      setFormData({ name: "", email: "", message: "" });
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-[#F3F4F6]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <p className="font-body text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
              Get In Touch
            </p>
            <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#0A0A0A] mb-6">
              Let&apos;s Build Something Great
            </h2>
            <p className="font-body text-lg text-[#6B7280] mb-8 max-w-lg">
              Ready to get started? Send us a message and we&apos;ll get back to you within 24 hours.
            </p>

            <div className="space-y-6">
              <a 
                href="tel:404.954.1182" 
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-12 h-12 bg-[#007AFF] flex items-center justify-center group-hover:bg-[#0A0A0A] transition-colors">
                  <Phone className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-body text-sm text-[#6B7280]">Call Us</p>
                  <p className="font-heading font-bold text-[#0A0A0A]">404.954.1182</p>
                </div>
              </a>
            </div>
          </div>

          <Card className="border-0 rounded-none shadow-xl" data-testid="contact-form-card">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                    Name
                  </label>
                  <Input 
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Your name"
                    required
                    className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                    Email
                  </label>
                  <Input 
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="your@email.com"
                    required
                    className={`rounded-none border-2 h-12 ${
                      formData.email && !isValidEmail(formData.email)
                        ? "border-red-500 focus:border-red-500"
                        : "border-[#E5E7EB] focus:border-[#007AFF]"
                    }`}
                    data-testid="contact-input-email"
                  />
                  {formData.email && !isValidEmail(formData.email) && (
                    <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
                  )}
                </div>
                <div>
                  <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                    Message
                  </label>
                  <Textarea 
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    placeholder="Tell us about your project..."
                    required
                    rows={5}
                    className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] resize-none"
                    data-testid="contact-input-message"
                  />
                </div>
                <Button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#007AFF] hover:bg-[#0055CC] text-white rounded-none py-6 font-medium"
                  data-testid="contact-submit-btn"
                >
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <span className="spinner"></span>
                      Sending...
                    </span>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

// Intake Form Section
const IntakeFormSection = () => {
  const [formData, setFormData] = useState({
    businessName: "",
    domain: "",
    tagline: "",
    address: "",
    phone: "",
    email: "",
    colorScheme: "",
    customColors: "",
    cashAppTag: "",
    wantsStripe: "",
    services: [{ name: "", price: "", duration: "" }],
    wantsTwilio: "",
    twilioNumbers: [""],
    wantsAiAgent: "",
    instagram: "",
    logoOption: "",
    logoFile: null,
    logoFileName: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("File too large. Maximum size is 5MB.");
        return;
      }
      // Check file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Invalid file type. Please upload an image (JPG, PNG, GIF, WebP) or PDF.");
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          logoFile: reader.result,
          logoFileName: file.name,
          logoOption: "have"
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const removeFile = () => {
    setFormData({
      ...formData,
      logoFile: null,
      logoFileName: ""
    });
  };

  const handleServiceChange = (index, field, value) => {
    const newServices = [...formData.services];
    newServices[index][field] = value;
    setFormData({ ...formData, services: newServices });
  };

  const addService = () => {
    setFormData({
      ...formData,
      services: [...formData.services, { name: "", price: "", duration: "" }]
    });
  };

  const removeService = (index) => {
    if (formData.services.length > 1) {
      const newServices = formData.services.filter((_, i) => i !== index);
      setFormData({ ...formData, services: newServices });
    }
  };

  const handleTwilioNumberChange = (index, value) => {
    // Only allow numbers, spaces, dashes, parentheses, and plus sign
    const cleaned = value.replace(/[^\d\s\-\(\)\+]/g, '');
    const newNumbers = [...formData.twilioNumbers];
    newNumbers[index] = cleaned;
    setFormData({ ...formData, twilioNumbers: newNumbers });
  };

  const isValidPhoneNumber = (number) => {
    // Remove all non-digits and check if at least 10 digits
    const digits = number.replace(/\D/g, '');
    return digits.length >= 10;
  };

  const addTwilioNumber = () => {
    setFormData({
      ...formData,
      twilioNumbers: [...formData.twilioNumbers, ""]
    });
  };

  const removeTwilioNumber = (index) => {
    if (formData.twilioNumbers.length > 1) {
      const newNumbers = formData.twilioNumbers.filter((_, i) => i !== index);
      setFormData({ ...formData, twilioNumbers: newNumbers });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email
    if (!isValidEmail(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    // Validate main phone number
    if (!isValidPhoneNumber(formData.phone)) {
      toast.error("Please enter a valid phone number (10+ digits)");
      return;
    }
    
    // Validate phone numbers if SMS is selected
    if (formData.wantsTwilio === "yes") {
      const invalidNumbers = formData.twilioNumbers.filter(n => n && !isValidPhoneNumber(n));
      if (invalidNumbers.length > 0) {
        toast.error("Please enter valid SMS phone numbers (10+ digits)");
        return;
      }
      const emptyNumbers = formData.twilioNumbers.filter(n => !n.trim());
      if (emptyNumbers.length === formData.twilioNumbers.length) {
        toast.error("Please enter at least one phone number for SMS");
        return;
      }
    }
    
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/intake`, formData);
      toast.success("Site request submitted! We'll be in touch within 24 hours.");
      setFormData({
        businessName: "",
        domain: "",
        tagline: "",
        address: "",
        phone: "",
        email: "",
        colorScheme: "",
        customColors: "",
        cashAppTag: "",
        wantsStripe: "",
        services: [{ name: "", price: "", duration: "" }],
        wantsTwilio: "",
        twilioNumbers: [""],
        wantsAiAgent: "",
        instagram: "",
        logoOption: "",
        logoFile: null,
        logoFileName: ""
      });
    } catch (error) {
      toast.error("Failed to submit. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const colorOptions = [
    { value: "earthy", label: "Earthy/Natural (greens, beiges)" },
    { value: "glam", label: "Glam/Luxe (gold, black)" },
    { value: "modern", label: "Modern/Clean (white, minimal)" },
    { value: "bold", label: "Bold/Vibrant (bright colors)" },
    { value: "custom", label: "Custom" }
  ];

  return (
    <section id="intake" className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="intake-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="text-center mb-12">
          <p className="font-body text-sm font-medium text-[#007AFF] uppercase tracking-widest mb-4">
            New Client
          </p>
          <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl tracking-tight text-white mb-4">
            New Site Request
          </h2>
          <p className="font-body text-lg text-[#9CA3AF] max-w-2xl mx-auto">
            Fill out this form to get your website started. We&apos;ll review and reach out within 24 hours.
          </p>
        </div>

        <Card className="border-0 rounded-none bg-white" data-testid="intake-form-card">
          <CardContent className="p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* Business Info */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  Business Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                      Business Name *
                    </label>
                    <Input 
                      type="text"
                      value={formData.businessName}
                      onChange={(e) => setFormData({...formData, businessName: e.target.value})}
                      placeholder="Your Business Name"
                      required
                      className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                      data-testid="intake-business-name"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                      Domain (if you have one)
                    </label>
                    <Input 
                      type="text"
                      value={formData.domain}
                      onChange={(e) => setFormData({...formData, domain: e.target.value})}
                      placeholder="yourbusiness.com"
                      className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                      data-testid="intake-domain"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                    Tagline / Slogan
                  </label>
                  <Input 
                    type="text"
                    value={formData.tagline}
                    onChange={(e) => setFormData({...formData, tagline: e.target.value})}
                    placeholder="Your catchy tagline"
                    className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                    data-testid="intake-tagline"
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  Contact Info
                </h3>
                <div>
                  <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                    Address
                  </label>
                  <Input 
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Main St, City, State ZIP"
                    className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                    data-testid="intake-address"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                      Phone *
                    </label>
                    <Input 
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const cleaned = e.target.value.replace(/[^\d\s\-\(\)\+]/g, '');
                        setFormData({...formData, phone: cleaned});
                      }}
                      placeholder="(555) 123-4567"
                      required
                      className={`rounded-none border-2 h-12 ${
                        formData.phone && !isValidPhoneNumber(formData.phone)
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#E5E7EB] focus:border-[#007AFF]"
                      }`}
                      data-testid="intake-phone"
                    />
                    {formData.phone && !isValidPhoneNumber(formData.phone) && (
                      <p className="text-red-500 text-xs mt-1">Please enter a valid phone number (10+ digits)</p>
                    )}
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                      Email *
                    </label>
                    <Input 
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      placeholder="you@email.com"
                      required
                      className={`rounded-none border-2 h-12 ${
                        formData.email && !isValidEmail(formData.email)
                          ? "border-red-500 focus:border-red-500"
                          : "border-[#E5E7EB] focus:border-[#007AFF]"
                      }`}
                      data-testid="intake-email"
                    />
                    {formData.email && !isValidEmail(formData.email) && (
                      <p className="text-red-500 text-xs mt-1">Please enter a valid email address</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Color Scheme */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  Color Scheme
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {colorOptions.map((option) => (
                    <label 
                      key={option.value}
                      className={`flex items-center gap-3 p-4 border-2 cursor-pointer transition-all ${
                        formData.colorScheme === option.value 
                          ? "border-[#007AFF] bg-blue-50" 
                          : "border-[#E5E7EB] hover:border-[#007AFF]"
                      }`}
                    >
                      <input
                        type="radio"
                        name="colorScheme"
                        value={option.value}
                        checked={formData.colorScheme === option.value}
                        onChange={(e) => setFormData({...formData, colorScheme: e.target.value})}
                        className="w-4 h-4 text-[#007AFF]"
                      />
                      <span className="font-body text-sm text-[#0A0A0A]">{option.label}</span>
                    </label>
                  ))}
                </div>
                {formData.colorScheme === "custom" && (
                  <Input 
                    type="text"
                    value={formData.customColors}
                    onChange={(e) => setFormData({...formData, customColors: e.target.value})}
                    placeholder="Describe your colors or enter hex codes (#FF5733)"
                    className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                    data-testid="intake-custom-colors"
                  />
                )}
              </div>

              {/* Payment */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  Payment Setup
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                      Cash App $Cashtag
                    </label>
                    <Input 
                      type="text"
                      value={formData.cashAppTag}
                      onChange={(e) => setFormData({...formData, cashAppTag: e.target.value})}
                      placeholder="$YourCashTag"
                      className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                      data-testid="intake-cashapp"
                    />
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                      Want Stripe Integration?
                    </label>
                    <div className="flex gap-4 mt-3">
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="wantsStripe"
                          value="yes"
                          checked={formData.wantsStripe === "yes"}
                          onChange={(e) => setFormData({...formData, wantsStripe: e.target.value})}
                          className="w-4 h-4 text-[#007AFF]"
                        />
                        <span className="font-body text-sm">Yes</span>
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="wantsStripe"
                          value="no"
                          checked={formData.wantsStripe === "no"}
                          onChange={(e) => setFormData({...formData, wantsStripe: e.target.value})}
                          className="w-4 h-4 text-[#007AFF]"
                        />
                        <span className="font-body text-sm">No</span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Services */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  Services (list with prices)
                </h3>
                {formData.services.map((service, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input 
                        type="text"
                        value={service.name}
                        onChange={(e) => handleServiceChange(index, "name", e.target.value)}
                        placeholder="Service name"
                        className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-10 text-sm"
                      />
                      <Input 
                        type="text"
                        value={service.price}
                        onChange={(e) => handleServiceChange(index, "price", e.target.value)}
                        placeholder="$Price"
                        className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-10 text-sm"
                      />
                      <Input 
                        type="text"
                        value={service.duration}
                        onChange={(e) => handleServiceChange(index, "duration", e.target.value)}
                        placeholder="Duration (mins)"
                        className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-10 text-sm"
                      />
                    </div>
                    {formData.services.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeService(index)}
                        className="p-2 text-red-500 hover:bg-red-50"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addService}
                  className="text-[#007AFF] font-medium text-sm hover:underline"
                >
                  + Add another service
                </button>
              </div>

              {/* Twilio SMS */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  SMS Notifications
                </h3>
                <p className="font-body text-sm text-[#6B7280]">
                  Want SMS reminders for appointments? ($10.88/year add-on)
                </p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="wantsTwilio"
                      value="yes"
                      checked={formData.wantsTwilio === "yes"}
                      onChange={(e) => setFormData({...formData, wantsTwilio: e.target.value})}
                      className="w-4 h-4 text-[#007AFF]"
                    />
                    <span className="font-body text-sm">Yes, I want SMS</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="wantsTwilio"
                      value="no"
                      checked={formData.wantsTwilio === "no"}
                      onChange={(e) => setFormData({...formData, wantsTwilio: e.target.value})}
                      className="w-4 h-4 text-[#007AFF]"
                    />
                    <span className="font-body text-sm">No thanks</span>
                  </label>
                </div>
                
                {formData.wantsTwilio === "yes" && (
                  <div className="space-y-3 mt-4 p-4 bg-[#F9FAFB] border border-[#E5E7EB]">
                    <label className="font-body text-sm font-medium text-[#0A0A0A] block">
                      Phone numbers to receive SMS notifications:
                    </label>
                    {formData.twilioNumbers.map((number, index) => (
                      <div key={index} className="flex gap-2 items-center">
                        <div className="flex-1">
                          <Input 
                            type="tel"
                            value={number}
                            onChange={(e) => handleTwilioNumberChange(index, e.target.value)}
                            placeholder="(555) 123-4567"
                            className={`rounded-none border-2 h-10 ${
                              number && !isValidPhoneNumber(number) 
                                ? "border-red-500 focus:border-red-500" 
                                : "border-[#E5E7EB] focus:border-[#007AFF]"
                            }`}
                          />
                          {number && !isValidPhoneNumber(number) && (
                            <p className="text-red-500 text-xs mt-1">Enter a valid phone number (10+ digits)</p>
                          )}
                        </div>
                        {formData.twilioNumbers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeTwilioNumber(index)}
                            className="p-2 text-red-500 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addTwilioNumber}
                      className="text-[#007AFF] font-medium text-sm hover:underline"
                    >
                      + Add another number
                    </button>
                  </div>
                )}
              </div>

              {/* AI Agent */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  AI Agent
                </h3>
                <p className="font-body text-sm text-[#6B7280]">
                  Want a custom AI assistant for your website? 24/7 customer support, answers FAQs, books appointments. ($288/month)
                </p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="wantsAiAgent"
                      value="yes"
                      checked={formData.wantsAiAgent === "yes"}
                      onChange={(e) => setFormData({...formData, wantsAiAgent: e.target.value})}
                      className="w-4 h-4 text-[#007AFF]"
                    />
                    <span className="font-body text-sm">Yes, I want an AI Agent</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="wantsAiAgent"
                      value="no"
                      checked={formData.wantsAiAgent === "no"}
                      onChange={(e) => setFormData({...formData, wantsAiAgent: e.target.value})}
                      className="w-4 h-4 text-[#007AFF]"
                    />
                    <span className="font-body text-sm">No thanks</span>
                  </label>
                </div>
              </div>

              {/* Extras */}
              <div className="space-y-4">
                <h3 className="font-heading font-bold text-xl text-[#0A0A0A] border-b border-[#E5E7EB] pb-2">
                  Extras
                </h3>
                <div>
                  <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                    Instagram Handle (for gallery import)
                  </label>
                  <Input 
                    type="text"
                    value={formData.instagram}
                    onChange={(e) => setFormData({...formData, instagram: e.target.value})}
                    placeholder="@yourbusiness"
                    className="rounded-none border-2 border-[#E5E7EB] focus:border-[#007AFF] h-12"
                    data-testid="intake-instagram"
                  />
                </div>
                
                <div>
                  <label className="font-body text-sm font-medium text-[#0A0A0A] mb-2 block">
                    Logo
                  </label>
                  <p className="font-body text-xs text-[#6B7280] mb-3">
                    We don't create logos. If you have one, upload it below.
                  </p>
                  <div className="flex gap-4 mb-4">
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="logoOption"
                        value="have"
                        checked={formData.logoOption === "have"}
                        onChange={(e) => setFormData({...formData, logoOption: e.target.value})}
                        className="w-4 h-4 text-[#007AFF]"
                      />
                      <span className="font-body text-sm">I have a logo</span>
                    </label>
                    <label className="flex items-center gap-2">
                      <input
                        type="radio"
                        name="logoOption"
                        value="none"
                        checked={formData.logoOption === "none"}
                        onChange={(e) => setFormData({...formData, logoOption: e.target.value, logoFile: null, logoFileName: ""})}
                        className="w-4 h-4 text-[#007AFF]"
                      />
                      <span className="font-body text-sm">I don't have a logo</span>
                    </label>
                  </div>
                  
                  {formData.logoOption === "have" && (
                    <div className="p-4 bg-[#F9FAFB] border border-[#E5E7EB] border-dashed">
                      {!formData.logoFile ? (
                        <div className="text-center">
                          <label className="cursor-pointer">
                            <div className="flex flex-col items-center gap-2 py-4">
                              <div className="w-12 h-12 bg-[#007AFF]/10 flex items-center justify-center rounded-full">
                                <ArrowRight className="h-6 w-6 text-[#007AFF] rotate-[-90deg]" />
                              </div>
                              <p className="font-body text-sm text-[#6B7280]">
                                Click to upload your logo
                              </p>
                              <p className="font-body text-xs text-[#9CA3AF]">
                                JPG, PNG, GIF, WebP or PDF (max 5MB)
                              </p>
                            </div>
                            <input
                              type="file"
                              accept="image/jpeg,image/png,image/gif,image/webp,application/pdf"
                              onChange={handleFileChange}
                              className="hidden"
                              data-testid="logo-upload"
                            />
                          </label>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between p-2 bg-white border border-[#E5E7EB]">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#007AFF]/10 flex items-center justify-center">
                              <CheckCircle className="h-5 w-5 text-[#007AFF]" />
                            </div>
                            <div>
                              <p className="font-body text-sm font-medium text-[#0A0A0A]">{formData.logoFileName}</p>
                              <p className="font-body text-xs text-[#6B7280]">Ready to upload</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeFile}
                            className="p-2 text-red-500 hover:bg-red-50"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <Button 
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#007AFF] hover:bg-[#0055CC] text-white rounded-none py-6 font-medium text-lg"
                data-testid="intake-submit-btn"
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2 justify-center">
                    <span className="spinner"></span>
                    Submitting...
                  </span>
                ) : (
                  <>
                    Submit Site Request
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#0A0A0A] py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <img
              src="/assets/logo-icon.png"
              alt="Acoretek Services"
              className="h-12 w-12 rounded-lg bg-white p-1.5 object-contain"
              data-testid="footer-logo"
            />
            <div>
              <p className="font-heading font-black text-xl text-white tracking-tighter">
                Acoretek Services
              </p>
              <p className="font-body text-sm text-[#6B7280] mt-1">
                Professional Web Development Services
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <a 
              href="tel:404.954.1182" 
              className="font-body text-sm text-[#9CA3AF] hover:text-white transition-colors"
            >
              404.954.1182
            </a>
            <a 
              href="mailto:support@acoretekservices.com" 
              className="font-body text-sm text-[#9CA3AF] hover:text-white transition-colors"
            >
              support@acoretekservices.com
            </a>
          </div>
        </div>
        <div className="border-t border-[#1F2937] mt-8 pt-8 text-center">
          <p className="font-body text-sm text-[#6B7280]">
            &copy; {new Date().getFullYear()} Acoretek Services. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Payment Success/Cancel Handler
const PaymentStatus = () => {
  const [status, setStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus({ type: "timeout", message: "Payment verification timed out. Please check your email for confirmation." });
      setIsPolling(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === "paid") {
        setStatus({ type: "success", message: "Payment successful! Thank you for your purchase." });
        toast.success("Payment successful! We'll be in touch soon.");
        setIsPolling(false);
        return;
      } else if (response.data.status === "expired") {
        setStatus({ type: "error", message: "Payment session expired. Please try again." });
        setIsPolling(false);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      setStatus({ type: "error", message: "Error verifying payment. Please contact support." });
      setIsPolling(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const success = params.get("success");
    const cancelled = params.get("cancelled");

    if (sessionId && success) {
      setIsPolling(true);
      setStatus({ type: "pending", message: "Verifying your payment..." });
      pollPaymentStatus(sessionId);
    } else if (cancelled) {
      setStatus({ type: "cancelled", message: "Payment was cancelled. No charges were made." });
      toast.info("Payment cancelled.");
    }
  }, [pollPaymentStatus]);

  if (!status) return null;

  return (
    <div 
      className={`fixed top-24 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-none shadow-xl max-w-md w-full mx-4 ${
        status.type === "success" ? "bg-green-50 border-l-4 border-green-500" :
        status.type === "error" || status.type === "timeout" ? "bg-red-50 border-l-4 border-red-500" :
        status.type === "cancelled" ? "bg-yellow-50 border-l-4 border-yellow-500" :
        "bg-blue-50 border-l-4 border-blue-500"
      }`}
      data-testid="payment-status"
    >
      <div className="flex items-center gap-3">
        {isPolling && <span className="spinner"></span>}
        <p className="font-body text-sm font-medium">
          {status.message}
        </p>
      </div>
    </div>
  );
};

// Main App Component
function App() {
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const handleCheckout = async (packageId) => {
    setIsCheckoutLoading(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/session`, {
        package_id: packageId,
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      toast.error("Failed to initiate checkout. Please try again.");
      setIsCheckoutLoading(false);
    }
  };

  const handleCryptoCheckout = async () => {
    setIsCheckoutLoading(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/checkout/crypto`, {
        package_id: "crypto_website",
        origin_url: originUrl
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      if (error.response?.status === 503) {
        toast.error("Crypto payments coming soon! Please use card payment for now.");
      } else {
        toast.error("Failed to initiate crypto checkout. Please try again.");
      }
      setIsCheckoutLoading(false);
    }
  };

  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-right" richColors />
      <PaymentStatus />
      <Navigation />
      <main>
        <HeroSection />
        <ServicesSection />
        <PricingSection 
          onCheckout={handleCheckout} 
          onCryptoCheckout={handleCryptoCheckout}
          isLoading={isCheckoutLoading} 
        />
        <FAQSection />
        <IntakeFormSection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
}

export default App;
